/**
 * Bulk property onboarding (Hotel Groups — Slice 2).
 *
 * The super-admin uploads a flat CSV/XLSX where EACH ROW is one property, then:
 *   1. `POST /v1/admin/platform/properties/bulk-import/validate` → a per-row
 *      preview report ({@link BulkImportReport}) with NO writes.
 *   2. `POST /v1/admin/platform/properties/bulk-import/commit` → creates the
 *      valid rows (partial-with-report; idempotent via `Idempotency-Key` +
 *      the `external_ref` / `name+address` natural key). Rows land as DRAFT.
 *
 * This is deliberately separate from the single-property questionnaire importer
 * ({@link PropertyImportPreview}) which is one property per file.
 */

/** Column headers of the bulk template (order = the generated CSV header row). */
export const BULK_IMPORT_COLUMNS = [
  'operator',
  'name',
  'address',
  'city',
  'country',
  'currency',
  'externalRef',
  'checkInTime',
  'checkOutTime',
  'petPolicy',
  'website',
  'zipCode',
  'amenities',
] as const;

/** Max rows accepted synchronously (async job for larger files is out of scope). */
export const BULK_IMPORT_MAX_ROWS = 1000;

/**
 * Per-row outcome.
 * - `valid`     — passes validation, is NEW, will be created (preview only).
 * - `exists`    — matches an existing property by the natural key (skipped).
 * - `duplicate` — a second row in THIS file with the same natural key (skipped).
 * - `error`     — failed per-row validation (missing field, bad currency, …).
 * - `created`   — created on commit (commit only).
 * - `failed`    — threw while committing (commit only; other rows unaffected).
 */
export type BulkRowStatus =
  | 'valid'
  | 'exists'
  | 'duplicate'
  | 'error'
  | 'created'
  | 'failed';

export interface BulkRowResult {
  /** 1-based spreadsheet row (header is row 1; the first data row is row 2). */
  row: number;
  name?: string;
  operator?: string;
  location?: string;
  status: BulkRowStatus;
  /** Human-readable reason for `error`/`failed`/`exists`/`duplicate`. */
  reason?: string;
  /** Present for `created` (and `exists`, echoing the matched property). */
  propertyId?: string;
}

export interface BulkImportSummary {
  total: number;
  valid: number;
  exists: number;
  duplicate: number;
  errors: number;
  /** Commit only — how many rows were actually created. */
  created?: number;
  /** Commit only — how many rows threw during their own transaction. */
  failed?: number;
  /** How many NEW operators (tenants) the file would create / created. */
  operatorsToCreate?: number;
}

export interface BulkImportReport {
  summary: BulkImportSummary;
  rows: BulkRowResult[];
}
